import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Vendor, PropertyVendor } from '../../types/providerSearch';
import { mockVendors, mockPropertyVendors } from '../../data/mockVendors';

// Search Context Interface
export interface SearchContext {
  userZip?: string;
  userCoordinates?: { lat: number; lon: number };
  city?: string;
  state?: string;
}

// Helper function to get default search type for each category
const getDefaultSearchType = (category: string): 'local' | 'name' => {
  switch (category) {
    case 'auto':
    case 'property':
      return 'local';
    default:
      return 'local';
  }
};

// State interface with proper typing
export interface ProviderSearchState {
  formData: Record<string, string>;
  isLoading: boolean;
  searchResults: (Vendor | PropertyVendor)[] | null;
  vendors: (Vendor | PropertyVendor)[];
  error: string | null;
  currentSearchCategory: 'auto' | 'property';
  externalSearchType: 'local' | 'name' | 'type' | 'region';
  searchContext: SearchContext;
  selectedRows: (Vendor | PropertyVendor)[];
  currentVendor: Vendor | PropertyVendor | null;
  isLoadingVendor: boolean;
  vendorError: string | null;
  isUpdatingVendor: boolean;
  updateVendorError: string | null;
  isAddingVendor: boolean;
  addVendorError: string | null;
  // Pagination metadata
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  // Mock vendor data for context-aware API calls
  autoMockVendors: Vendor[];
  propertyMockVendors: PropertyVendor[];
}

// Initial state with proper typing
const initialState: ProviderSearchState = {
  formData: {},
  isLoading: false,
  searchResults: null,
  error: null,
  currentSearchCategory: 'auto',
  externalSearchType: 'local',
  searchContext: {},
  selectedRows: [],
  currentVendor: null,
  isLoadingVendor: false,
  vendorError: null,
  isUpdatingVendor: false,
  updateVendorError: null,
  isAddingVendor: false,
  addVendorError: null,
  vendors: [],
  // Pagination
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  totalPages: 0,
  // Initialize mock vendor arrays with actual data
  //vendors: [], // or initialize with mockVendors if appropriate
  // Initialize mock vendor arrays with actual data
  autoMockVendors: mockVendors,
  propertyMockVendors: mockPropertyVendors
};

const providerSearchSlice = createSlice({
  name: 'providerSearch',
  initialState,
  reducers: {
    // Form management
    updateField: (state, action: PayloadAction<{ name: string; value: string }>) => {
      state.formData[action.payload.name] = action.payload.value;
    },
    resetForm: (state) => {
      state.formData = {};
      state.error = null;
    },

    // Loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Search actions
    searchRequest: (state, _action: PayloadAction<Record<string, string>>) => {
      state.isLoading = true;
      state.error = null;
    },
    searchSuccess: (state, action: PayloadAction<{
      results: (Vendor | PropertyVendor)[],
      totalCount?: number,
      currentPage?: number,
      pageSize?: number,
      totalPages?: number
    }>) => {

      state.isLoading = false;
      state.searchResults = action.payload.results;
      state.error = null;
      // Update pagination metadata if provided
      if (action.payload.totalCount !== undefined) {
        state.totalCount = action.payload.totalCount;
      }
      if (action.payload.currentPage !== undefined) {
        state.currentPage = action.payload.currentPage;
      }
      if (action.payload.pageSize !== undefined) {
        state.pageSize = action.payload.pageSize;
      }
      if (action.payload.totalPages !== undefined) {
        state.totalPages = action.payload.totalPages;
      }
    },
    searchFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.searchResults = null;
    },

    // Error handling
    clearError: (state) => {
      state.error = null;
    },

    // Search results management
    setSearchResults: (state, action: PayloadAction<(Vendor | PropertyVendor)[]>) => {
      state.searchResults = action.payload;
      state.isLoading = false;
    },
    clearSearchResults: (state) => {
      state.searchResults = null;
    },
    // Search context management
    setSearchContext: (state, action: PayloadAction<SearchContext>) => {
      state.searchContext = {
        ...state.searchContext,
        ...action.payload,
      };
    },
    // Search category management
    setSearchCategory: (state, action: PayloadAction<'auto' | 'property'>) => {
      // Only clear results if category is actually changing
      if (state.currentSearchCategory !== action.payload) {
        state.searchResults = null;
      }
      state.currentSearchCategory = action.payload;
      state.externalSearchType = getDefaultSearchType(action.payload);
    },
    setExternalSearchType: (state, action: PayloadAction<'local' | 'name' | 'type' | 'region'>) => {
      state.externalSearchType = action.payload;
      state.searchResults = null;
      state.selectedRows = [];
    },


    setVendors: (state, action: PayloadAction<(Vendor | PropertyVendor)[]>) => {
      state.vendors = action.payload;
    },

    // Selected rows management
    setSelectedRows: (state, action: PayloadAction<(Vendor | PropertyVendor)[]>) => {
      state.selectedRows = action.payload;
    },
    clearSelectedRows: (state) => {
      state.selectedRows = [];
    },

    // Individual vendor fetch actions (moved from actiontypes.ts)
    fetchVendorById: (state, _action: PayloadAction<string>) => {
      state.isLoadingVendor = true;
      state.vendorError = null;
      //state.currentVendor = null; // Clear previous vendor data
    },
    fetchVendorByIdSuccess: (state, action: PayloadAction<Vendor | PropertyVendor>) => {
      state.isLoadingVendor = false;
      state.currentVendor = action.payload;
    },
    fetchVendorByIdFailure: (state, action: PayloadAction<string>) => {
      state.isLoadingVendor = false;
      state.vendorError = action.payload;
      state.currentVendor = null; // Only clear here on actual failure
    },
  },
});

export const {
  // Export all actions
  updateField,
  resetForm,
  setLoading,
  searchRequest,
  searchSuccess,
  searchFailure,
  clearError,
  setSearchResults,
  clearSearchResults,
  setSearchContext,
  setSelectedRows,
  clearSelectedRows,
  setSearchCategory,
  setExternalSearchType,
  fetchVendorById,
  fetchVendorByIdSuccess,
  fetchVendorByIdFailure,
  setVendors // Add this line
} = providerSearchSlice.actions;

export default providerSearchSlice.reducer;
